/**
 * TopBar — Horizontal top bar with breadcrumbs, search, and chat toggle.
 *
 * Props:
 *   chatOpen: boolean — whether chat panel is open
 *   onToggleChat: () => void — toggle chat panel
 *   breadcrumbs: Array<{ label, path? }> — breadcrumb trail (optional)
 *   actions: ReactNode — additional action buttons (optional)
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import defaultNavigationSections from './navigationConfig';
import styles from './TopBar.module.css';

// Title-case a word, treating snake_case as space-separated.
// Used as a fallback when the path doesn't match anything in the nav config.
function humanize(segment) {
  if (!segment) return '';
  return segment
    .replace(/_/g, ' ')
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Find the nav item whose path best matches the current location, so the
// breadcrumb label exactly matches the side-nav label (e.g.,
// "/report_sankey_analysis/CATEGORY" → "Transaction Analysis").
function findNavMatch(pathname) {
  let best = null;
  for (const section of defaultNavigationSections) {
    for (const item of section.items) {
      if (!item.path) continue;
      if (pathname === item.path) return item; // exact match wins
      // Prefix match — pick the longest one when multiple share a prefix.
      if (pathname.startsWith(item.path + '/')) {
        if (!best || item.path.length > best.path.length) best = item;
      }
    }
  }
  return best;
}

function TopBar({ chatOpen, onToggleChat, breadcrumbs, actions, pendingActions = 0, showChatToggle = true }) {
  const location = useLocation();

  // Derive breadcrumbs from the nav config so labels match the side nav.
  let derivedBreadcrumbs;
  if (location.pathname === '/') {
    derivedBreadcrumbs = [{ label: 'Dashboard' }];
  } else {
    const match = findNavMatch(location.pathname);
    if (match) {
      derivedBreadcrumbs = [{ label: match.displayName }];
    } else {
      // Fallback: take the first non-empty path segment and humanize it.
      const firstSegment = location.pathname.split('/').filter(Boolean)[0] || '';
      derivedBreadcrumbs = [{ label: humanize(firstSegment) }];
    }
  }
  const displayBreadcrumbs = breadcrumbs || derivedBreadcrumbs;

  return (
    <header className={styles.topBar}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
        {displayBreadcrumbs.map((crumb, idx) => (
          <React.Fragment key={idx}>
            <span className={styles.breadcrumbSep}>/</span>
            <span className={idx === displayBreadcrumbs.length - 1 ? styles.breadcrumbCurrent : styles.breadcrumbItem}>
              {crumb.label}
            </span>
          </React.Fragment>
        ))}
      </nav>

      <div className={styles.spacer} />

      <div className={styles.actions}>
        {/* Extra actions */}
        {actions}

        {/* LIVI toggle (super-admin only during testing) */}
        {showChatToggle && (
          <button
            className={`${styles.chatToggle} ${chatOpen ? styles.chatToggleActive : ''}`}
            onClick={onToggleChat}
            aria-label={chatOpen ? 'Close Livi AI' : 'Open Livi AI'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z"/>
            </svg>
            <span className={styles.chatToggleLabel}>Livi AI</span>
            {pendingActions > 0 && (
              <span className={styles.chatToggleBadge}>{pendingActions}</span>
            )}
          </button>
        )}
      </div>
    </header>
  );
}

export default TopBar;
